import React, { useState, useCallback, useEffect } from 'react';
import api from '../services/api';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const BASE_PATH = window.location.pathname.startsWith('/ecommerzaa') ? '/ecommerzaa' : '';

function getDateRange(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const fmt = (dt) => dt.toISOString().split('T')[0];

  switch (preset) {
    case 'this_month':
      return { from: fmt(new Date(y, m, 1)), to: fmt(now) };
    case 'last_month': {
      const last = new Date(y, m - 1, 1);
      const lastEnd = new Date(y, m, 0);
      return { from: fmt(last), to: fmt(lastEnd) };
    }
    case 'this_quarter': {
      const qStart = Math.floor(m / 3) * 3;
      return { from: fmt(new Date(y, qStart, 1)), to: fmt(now) };
    }
    case 'last_quarter': {
      const lqStart = Math.floor(m / 3) * 3 - 3;
      const lqEnd = new Date(y, lqStart + 3, 0);
      return { from: fmt(new Date(y, lqStart, 1)), to: fmt(lqEnd) };
    }
    case 'this_fy': {
      const fyStart = m >= 3 ? y : y - 1;
      return { from: `${fyStart}-04-01`, to: fmt(now) };
    }
    case 'last_fy': {
      const fyStart = m >= 3 ? y - 1 : y - 2;
      return { from: `${fyStart}-04-01`, to: `${fyStart + 1}-03-31` };
    }
    case 'this_year':
      return { from: `${y}-01-01`, to: fmt(now) };
    case 'last_year':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    default:
      return { from: fmt(new Date(y, 0, 1)), to: fmt(now) };
  }
}

const GstReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [datePreset, setDatePreset] = useState('this_fy');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [groupBy, setGroupBy] = useState('month');
  const [exportFormat, setExportFormat] = useState('csv');
  const [returnFilter, setReturnFilter] = useState('all');

  const { from: initFrom, to: initTo } = getDateRange('this_fy');
  const [from, setFrom] = useState(initFrom);
  const [to, setTo] = useState(initTo);

  const applyPreset = (preset) => {
    if (preset === 'custom') {
      setIsCustom(true);
      return;
    }
    setIsCustom(false);
    setDatePreset(preset);
    const range = getDateRange(preset);
    setFrom(range.from);
    setTo(range.to);
  };

  const applyCustom = () => {
    if (customFrom && customTo) {
      setFrom(customFrom);
      setTo(customTo);
      setIsCustom(true);
    }
  };

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/gst/detailed-report.php?from=${from}&to=${to}&group_by=${groupBy}&return_status=${returnFilter}`);
      if (res.data.success) setData(res.data.data);
      else setError(res.data.message);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load GST report');
    } finally {
      setLoading(false);
    }
  }, [from, to, groupBy, returnFilter]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = () => {
    if (exportFormat === 'pdf') { window.print(); return; }
    const ext = exportFormat === 'xls' ? 'xls' : exportFormat === 'doc' ? 'doc' : 'csv';
    const params = `from=${from}&to=${to}&group_by=${groupBy}&format=${exportFormat === 'csv' ? 'csv' : 'html'}` + (exportFormat !== 'csv' ? `&export_ext=${ext}` : '');
    window.open(`${BASE_PATH}/backend/public/api/gst/detailed-report.php?${params}`, '_blank');
  };

  if (loading && !data) return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading GST report...</div>;

  const s = data?.summary || {};
  const details = data?.details || [];

  const presets = [
    { key: 'this_month', label: 'This Month' },
    { key: 'last_month', label: 'Last Month' },
    { key: 'this_quarter', label: 'This Quarter' },
    { key: 'last_quarter', label: 'Last Quarter' },
    { key: 'this_fy', label: 'This FY' },
    { key: 'last_fy', label: 'Last FY' },
    { key: 'this_year', label: 'This Year' },
    { key: 'last_year', label: 'Last Year' },
    { key: 'custom', label: 'Custom' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">GST Report</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Output GST (Sales) vs Input GST (Purchases) | {from} to {to}</p>
        </div>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">{error}</div>}

      {/* Date Presets */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Date Range</label>
        <div className="flex flex-wrap gap-2 mb-3">
          {presets.map(p => (
            <button key={p.key} onClick={() => applyPreset(p.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                (isCustom ? p.key === 'custom' : datePreset === p.key)
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}>{p.label}</button>
          ))}
        </div>
        {isCustom && (
          <div className="flex items-end gap-3 mt-2">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">From</label>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">To</label>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" />
            </div>
            <button onClick={applyCustom}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm transition-colors">Apply Date</button>
          </div>
        )}
      </div>

      {/* More Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Group By</label>
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
              <option value="month">Month</option>
              <option value="category">Category</option>
              <option value="hsn">HSN Code</option>
              <option value="platform">Platform</option>
              <option value="detail">Detail (Orders)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Return Status</label>
            <select value={returnFilter} onChange={e => setReturnFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
              <option value="all">All Orders</option>
              <option value="not_returned">Not Returned</option>
              <option value="returned">Returned Only</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Export As</label>
            <div className="flex gap-1">
              <select value={exportFormat} onChange={e => setExportFormat(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
                <option value="csv">CSV</option>
                <option value="xls">Excel</option>
                <option value="doc">DOC</option>
                <option value="pdf">PDF (Print)</option>
              </select>
              <button onClick={handleExport}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm transition-colors whitespace-nowrap">Export</button>
            </div>
          </div>
          <button onClick={fetchReport}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors">Refresh</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Invoices</p>
          <p className="text-xl font-bold text-gray-800 dark:text-white">{s.invoice_count || 0}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Sales (incl. GST)</p>
          <p className="text-xl font-bold text-blue-600">₹{(s.total_sales || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Taxable Amount</p>
          <p className="text-xl font-bold text-indigo-600">₹{(s.taxable_amount || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Output GST</p>
          <p className="text-xl font-bold text-purple-600">₹{(s.total_gst || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Input GST (ITC)</p>
          <p className="text-xl font-bold text-teal-600">₹{(s.input_gst || 0).toFixed(2)}</p>
        </div>
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 ${(s.net_gst || 0) > 0 ? 'border-l-4 border-red-500' : (s.net_gst || 0) < 0 ? 'border-l-4 border-green-500' : ''}`}>
          <p className="text-xs text-gray-500 dark:text-gray-400">Net GST Payable</p>
          <p className={`text-xl font-bold ${(s.net_gst || 0) > 0 ? 'text-red-600' : (s.net_gst || 0) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
            ₹{(s.net_gst || 0).toFixed(2)}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">CGST</p>
          <p className="text-lg font-bold text-green-600">₹{(s.cgst || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">SGST</p>
          <p className="text-lg font-bold text-green-600">₹{(s.sgst || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">IGST</p>
          <p className="text-lg font-bold text-purple-600">₹{(s.igst || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border-l-4 border-amber-500">
          <p className="text-xs text-gray-500 dark:text-gray-400">Returned Orders</p>
          <p className="text-lg font-bold text-amber-600">{s.returned_count || 0}</p>
          {s.total_refunds > 0 && <p className="text-xs text-amber-500">₹{s.total_refunds.toFixed(2)} refunded</p>}
        </div>
      </div>

      {/* Detail Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            {groupBy === 'month' ? 'Monthly Breakdown' : groupBy === 'category' ? 'Category-wise Breakdown' : groupBy === 'platform' ? 'Platform-wise Breakdown' : groupBy === 'detail' ? 'Order-wise Details' : 'HSN Code-wise Breakdown'}
          </h3>
          <span className="text-sm text-gray-500">{details.length} rows</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b">
              <tr>
                {groupBy === 'month' && <><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">States</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GST Type</th></>}
                {groupBy === 'category' && <><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">HSN Code</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">States</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GST Rate</th></>}
                {groupBy === 'hsn' && <><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">HSN Code</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">States</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GST Rate</th></>}
                {groupBy === 'platform' && <><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">States</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GST Type</th></>}
                {groupBy === 'detail' && <><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th><th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Return</th></>}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoices</th>
                {groupBy !== 'month' && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Items</th>}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{groupBy === 'detail' ? 'Amount' : 'Total Sales'}</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Taxable</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">CGST</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">SGST</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">IGST</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total GST</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {details.length === 0 ? (
                <tr><td colSpan={groupBy === 'month' ? 11 : groupBy === 'category' || groupBy === 'hsn' ? 13 : groupBy === 'platform' ? 11 : groupBy === 'detail' ? 14 : 9} className="px-6 py-12 text-center text-gray-500">No data found for selected period.</td></tr>
              ) : details.map((row, i) => {
                const label = groupBy === 'month'
                  ? (() => { if (!row.period) return 'N/A'; const parts = row.period.split('-'); if (parts.length < 2) return row.period; const [yy, mm] = parts; return `${MONTHS[parseInt(mm)-1] || mm} ${yy}`; })()
                  : groupBy === 'category' ? row.category_name
                  : groupBy === 'platform' ? (row.platform_name || '').charAt(0).toUpperCase() + (row.platform_name || '').slice(1)
                  : groupBy === 'detail' ? row.order_no
                  : row.hsn_code;
                const sublabel = groupBy === 'hsn' ? row.category_name : null;
                const rowBg = groupBy === 'detail'
                  ? (row.is_returned ? 'bg-red-50 dark:bg-red-950/20' : 'bg-green-50/50 dark:bg-green-950/10')
                  : '';
                return (
                  <tr key={i} className={`${rowBg} hover:bg-gray-100 dark:hover:bg-gray-700/50`}>
                    {groupBy === 'month' && <><td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{label}</td><td className="px-4 py-3 text-sm capitalize text-gray-600">{row.shipping_state || 'Unknown'}</td><td className="px-4 py-3 text-sm capitalize text-gray-600">{row.gst_type || '-'}</td></>}
                    {groupBy === 'category' && <><td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{label}</td><td className="px-4 py-3 text-sm font-mono text-gray-500">{row.hsn_code || '-'}</td><td className="px-4 py-3 text-sm capitalize text-gray-600">{row.shipping_state || 'Unknown'}</td><td className="px-4 py-3 text-sm text-gray-600">{row.gst_rate_value && row.gst_rate_value !== '0' ? `${row.gst_rate_value}%` : row.gst_rate_name || '-'}</td></>}
                    {groupBy === 'hsn' && <><td className="px-4 py-3 text-sm font-mono font-medium text-gray-900 dark:text-white">{label}</td><td className="px-4 py-3 text-sm text-gray-500">{sublabel}</td><td className="px-4 py-3 text-sm capitalize text-gray-600">{row.shipping_state || 'Unknown'}</td><td className="px-4 py-3 text-sm text-gray-600">{row.gst_rate_value && row.gst_rate_value !== '0' ? `${row.gst_rate_value}%` : row.gst_rate_name || '-'}</td></>}
                    {groupBy === 'platform' && <><td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white capitalize">{label}</td><td className="px-4 py-3 text-sm capitalize text-gray-600">{row.shipping_state || 'Unknown'}</td><td className="px-4 py-3 text-sm capitalize text-gray-600">{row.gst_type || '-'}</td></>}
                    {groupBy === 'detail' && <>
                      <td className="px-4 py-3 text-sm font-medium text-blue-600">{label}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{row.customer_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{row.order_date ? new Date(row.order_date).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'}) : '-'}</td>
                      <td className="px-4 py-3 text-sm capitalize text-gray-600">{row.platform_name || 'Direct'}</td>
                      <td className="px-4 py-3 text-sm capitalize text-gray-600">{row.shipping_state || 'Unknown'}</td>
                      <td className="px-4 py-3 text-center">
                        {row.is_returned
                          ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border border-red-200 dark:border-red-800">Returned</span>
                          : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-800">No Return</span>}
                      </td>
                    </>}
                    {groupBy === 'detail'
                      ? <td className="px-4 py-3 text-sm text-right text-gray-600">1</td>
                      : <td className="px-4 py-3 text-sm text-right text-gray-600">{row.invoice_count}</td>}
                    {groupBy !== 'month' && <td className="px-4 py-3 text-sm text-right text-gray-600">{row.item_count}</td>}
                    {groupBy === 'detail'
                      ? <td className="px-4 py-3 text-sm text-right text-blue-600 font-medium">₹{(row.total_amount ?? 0).toFixed(2)}</td>
                      : <td className="px-4 py-3 text-sm text-right text-blue-600 font-medium">₹{(row.total_sales ?? 0).toFixed(2)}</td>}
                    <td className="px-4 py-3 text-sm text-right text-indigo-600">₹{(row.taxable_amount ?? 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-green-600">₹{(row.cgst ?? 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-green-600">₹{(row.sgst ?? 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-purple-600">₹{(row.igst ?? 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 dark:text-white">₹{(row.total_gst ?? 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            {details.length > 0 && (
              <tfoot className="bg-gray-50 dark:bg-gray-700 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
                <tr>
                  {groupBy === 'month' && <td colSpan="3" className="px-4 py-3 text-sm text-gray-800 dark:text-white">Total</td>}
                  {groupBy === 'category' && <td colSpan="4" className="px-4 py-3 text-sm text-gray-800 dark:text-white">Total</td>}
                  {groupBy === 'hsn' && <td colSpan="4" className="px-4 py-3 text-sm text-gray-800 dark:text-white">Total</td>}
                  {groupBy === 'platform' && <td colSpan="3" className="px-4 py-3 text-sm text-gray-800 dark:text-white">Total</td>}
                  {groupBy === 'detail' && <td colSpan="6" className="px-4 py-3 text-sm text-gray-800 dark:text-white">Total ({details.length} orders)</td>}
                  {groupBy === 'detail'
                    ? <td className="px-4 py-3 text-sm text-right text-gray-800 dark:text-white">{details.length}</td>
                    : <td className="px-4 py-3 text-sm text-right text-gray-800 dark:text-white">{details.reduce((a, r) => a + r.invoice_count, 0)}</td>}
                  {groupBy !== 'month' && <td className="px-4 py-3 text-sm text-right text-gray-800 dark:text-white">{details.reduce((a, r) => a + (r.item_count || 0), 0)}</td>}
                  {groupBy === 'detail'
                    ? <td className="px-4 py-3 text-sm text-right text-blue-600">₹{details.reduce((a, r) => a + (r.total_amount ?? 0), 0).toFixed(2)}</td>
                    : <td className="px-4 py-3 text-sm text-right text-blue-600">₹{details.reduce((a, r) => a + (r.total_sales ?? 0), 0).toFixed(2)}</td>}
                  <td className="px-4 py-3 text-sm text-right text-indigo-600">₹{details.reduce((a, r) => a + (r.taxable_amount ?? 0), 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-right text-green-600">₹{details.reduce((a, r) => a + (r.cgst ?? 0), 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-right text-green-600">₹{details.reduce((a, r) => a + (r.sgst ?? 0), 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-right text-purple-600">₹{details.reduce((a, r) => a + (r.igst ?? 0), 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 dark:text-white">₹{details.reduce((a, r) => a + (r.total_gst ?? 0), 0).toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 text-sm text-gray-500 dark:text-gray-400 space-y-1">
        <p><strong>Note:</strong> Sales amounts include GST. CGST &amp; SGST apply to intra-state, IGST to inter-state transactions.</p>
        {groupBy === 'detail' && <p><strong>Return Status:</strong> <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">No Return</span> = order not returned &nbsp; <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Returned</span> = order has active return.</p>}
      </div>
    </div>
  );
};

export default GstReport;
